<!-- eslint-disable vue/no-mutating-props -->
<template>
    <v-container fluid>
        <v-row>
            <v-col cols="3">
                <v-text-field
                    v-model="complaint.complaintTypeName"
                    label="Typ reklamacji"
                    required
                    readonly
                />
            </v-col>
        </v-row>

        <ProductionSection
            :complaint="complaint"
            :validation="validationState"
            :store-module-name="storeModuleName"
            :is-read-only="isReadOnly"
            @field-blur="emitFieldBlur"
        />

        <JobSection
            :complaint="complaint"
            :validation="validationState"
            :store-module-name="storeModuleName"
            :is-read-only="isReadOnly"
            @field-blur="emitFieldBlur"
        />
        
        <v-row>
            <v-col cols="3">
                <v-autocomplete
                    v-model="complaint.discrepancyCatcherId"
                    :items="users"
                    label="Kto wyłapał błąd"
                    :item-value="userId"
                    :item-text="userFullName"
                    :no-data-text="userSearchNoDataText"
                    clearable
                    :loading="userSearchData.isLoading"
                    :search-input.sync="userSearchData.search"
                    :error="validationState.discrepancyCatcherId.error"
                    :error-messages="validationState.discrepancyCatcherId.messages"
                    :disabled="isReadOnly"
                    @blur="emitFieldBlur('discrepancyCatcherId')"
                />
            </v-col>
        </v-row>

        <ErrorsSection
            :complaint="complaint"
            :validation="validationState"
            :is-read-only="isReadOnly"
            @field-blur="emitFieldBlur"
        />

        <v-row>
            <v-col cols="9">
                <v-textarea
                    v-model="complaint.complaintProductDescription"
                    label="Opis reklamowanej części"
                    outlined
                    no-resize
                    :rows="4"
                    :counter="1000"
                    :error="validationState.complaintProductDescription.error"
                    :error-messages="validationState.complaintProductDescription.messages"
                    :disabled="isReadOnly"
                    @blur="emitFieldBlur('complaintProductDescription')"
                />
            </v-col>
        </v-row>

        <DefectsSection
            :complaint="complaint"
            :is-read-only="isReadOnly"
        />

        <v-row>
            <v-col cols="9">
                <v-autocomplete
                    v-model="complaint.responsiblePerson"
                    :items="responsibleUsers"
                    label="Osoby odpowiedzialne"
                    item-value="id"
                    :item-text="userFullName"
                    :no-data-text="responsibleUserSearchNoDataText"
                    clearable
                    multiple
                    chips
                    :loading="responsibleUserSearchData.isLoading"
                    :search-input.sync="responsibleUserSearchData.search"
                    :error="validationState.responsiblePerson.error"
                    :error-messages="validationState.responsiblePerson.messages"
                    :disabled="isReadOnly"
                    @blur="emitFieldBlur('responsiblePerson')"
                />
            </v-col>
        </v-row>
        
        <WayOfProceedingSection
            :complaint="complaint"
            :validation="validationState"
            :is-read-only="isReadOnly"
            @field-blur="emitFieldBlur"
        />

        <v-row v-if="isEditAction">
            <v-col cols="3">
                <v-select
                    v-model="complaint.complaintStatusId"
                    :items="statuses"
                    label="Status"
                    item-value="id"
                    item-text="name"
                    no-data-text="Nie znaleziono statusów"
                    clearable
                    :disabled="isStatusDisabled"
                    :hint="statusHint"
                    :persistent-hint="Boolean(statusHint)"
                />
            </v-col>
        </v-row>
    </v-container>
</template>


<script>
import ProductionSection from './ProductionSection.vue'
import ErrorsSection from './ErrorsSection.vue'
import WayOfProceedingSection from './WayOfProceedingSection.vue'
import JobSection from './JobSection.vue'
import DefectsSection from './DefectsSection.vue'
import { createSearchState } from '../../helpers/searchState'
import { STATUSES } from '../../helpers/constants'

/* eslint-disable vue/no-mutating-props */
export default {
    name: 'DetailsTab',

    components: {
        ProductionSection,
        ErrorsSection,
        WayOfProceedingSection,
        JobSection,
        DefectsSection,
    },

    props: {
        complaint: {
            type: Object,
            default: null,
        },
        validation: {
            type: Object,
            default: () => ({}),
        },
        storeModuleName: {
            type: String,
            default: ''
        },
        hasBasicUpdatePermission: {
            type: Boolean
        },
        hasUpdatePermission: {
            type: Boolean
        },
        isEditAction: {
            type: Boolean,
        },
        isReadOnly: {
            type: Boolean,
        },
        isCausesTabComplete: {
            type: Boolean,
        },
    },

    data() {
        return {
            userSearchData: createSearchState(),
            responsibleUserSearchData: createSearchState(),
        }
    },

    computed: {
        validationState() {
            const fields = [
                'complaintProductionDepartmentId',
                'jobNumber',
                'productionSiteGroupId',
                'productionProcessId',
                'machineId',
                'discrepancyCatcherId',
                'complaintProductDescription',
                'responsiblePerson',
                'errorCodeGroupId',
                'errorCodeId',
                'complaintWayOfProceedingId',
                'complaintWayOfProceedingValue',
            ]
            const provided = this.validation || {}

            return fields.reduce((acc, field) => {
                const entry = provided[field] || {}

                acc[field] = {
                    error: Boolean(entry.error),
                    messages: Array.isArray(entry.messages) ? entry.messages : []
                }

                return acc
            }, {})
        },

        statuses() {
            return this.$store.state.complaints.internalComplaints.statuses
        },

        isStatusDisabled() {
            if (this.isReadOnly) return true

            if (!this.hasUpdatePermission) return true

            return false
        },

        isStatusInvalid() {
            const selectedStatusId = this.complaint.complaintStatusId

            return selectedStatusId === STATUSES.ACCEPTED && !this.isCausesTabComplete
        },

        statusErrorMessages() {
            if (this.isStatusInvalid) {
                return ['Nie można zmienić statusu na "Uznana" bez uzupełnienia wszystkich pól w zakładce "Przyczyny".']
            }

            return []
        },

        statusHint() {
            if (this.isReadOnly && !this.hasUpdatePermission) {
                return 'Zmiany statusu wymagają uprawnienia internal_complaints_update.'
            }

            return ''
        },

        users() {
            return this.$store.state.complaints.internalComplaints.users
        },

        responsibleUsers() {
            return this.$store.state.complaints.internalComplaints.responsibleUsers
        },

        loggedUser() {
            return this.$store.state.user
        },

        userFullName() {
            return user => `${user.firstName || user.userFirstName} ${user.lastName || user.userLastName}`.trim()
        },

        userId() {
            return user => user.id || user.userId
        },

        userSearchNoDataText() {
            if (this.userSearchData.isLoading) return 'Szukanie...'

            const search = this.userSearchData.search

            return !search || search.length < 3
                ? 'Wpisz conajmniej 3 znaki żeby wyszukać'
                : 'Nie znaleziono użytkowników'
        },

        responsibleUserSearchNoDataText() {
            if (this.responsibleUserSearchData.isLoading) return 'Szukanie...'

            const search = this.responsibleUserSearchData.search

            return !search || search.length < 3
                ? 'Wpisz conajmniej 3 znaki żeby wyszukać'
                : 'Nie znaleziono użytkowników'
        },
    },

    watch: {
        'userSearchData.search'(value) {
            clearTimeout(this.userSearchData.timer)
            this.userSearchData.timer = setTimeout(async() => {
                const search = (value || '').trim()

                const loggedUser = this.loggedUser
                const loggedUserLabel = `${loggedUser?.userFirstName} ${loggedUser?.userLastName}`

                const seededOnlyLoggedUser = this.users.length === 1 && (
                    this.users[0].id === loggedUser?.userId ||
                    this.users[0].userId === loggedUser?.userId
                )

                const shouldSkip = (
                    search.length < 3 ||
                    search === this.userSearchData.lastQuery ||
                    (seededOnlyLoggedUser && search === loggedUserLabel) ||
                    (seededOnlyLoggedUser && search.length === 0)
                )

                if (shouldSkip) return

                this.userSearchData.isLoading = true

                await this.$store.dispatch(`${this.storeModuleName}/getUsers`, { search, type: 'users' })
                
                this.userSearchData.lastQuery = search
                this.userSearchData.isLoading = false
            }, this.userSearchData.debounceTimer)
        },

        'responsibleUserSearchData.search'(value) {
            clearTimeout(this.responsibleUserSearchData.timer)
            this.responsibleUserSearchData.timer = setTimeout(async() => {
                const search = (value || '').trim()

                if (search.length < 3 || search === this.responsibleUserSearchData.lastQuery) return

                this.responsibleUserSearchData.isLoading = true
                await this.$store.dispatch(`${this.storeModuleName}/getUsers`, { search, type: 'responsibleUsers' })
                this.responsibleUserSearchData.lastQuery = search
                this.responsibleUserSearchData.isLoading = false
            }, this.responsibleUserSearchData.debounceTimer)
        },

    },

    mounted() {
        if (!this.complaint.discrepancyCatcherId) {
            const loggedUser = this.loggedUser
            
            if (loggedUser) {
                const current = this.$store.state.complaints.internalComplaints.users
                
                if (!current.length || !(current[0]?.id === loggedUser.userId || current[0]?.userId === loggedUser.userId)) {
                    this.$store.state.complaints.internalComplaints.users = [loggedUser]
                }
                
                this.complaint.discrepancyCatcherId = loggedUser.userId
            }
        }
    },

    methods: {
        emitFieldBlur(field) {
            this.$emit('field-blur', field)
        },
    },
}
</script>
